import {
  FileUpload,
  FileUploadProps,
  message,
  UploadFile,
  Image,
} from "@gw/web-basic-components";
import React, { useEffect, useMemo, useRef, useState } from "react";
import type { IAttachmentUploadProps } from "./dto";
import classNames from "classnames";
import { useRequest, apis } from "../../utils";
import {
  isAcceptFile,
  getSuffixNamesByAccept,
  toLowerCaseSuffix,
  previewWithNewWindow,
  splitFileName,
  renderTips,
  download,
} from "./utils";
import { isNullOrUndefined } from "../utils";

import FileZip from "./assets/file-zip.png";

import "./index.less";

const defaultConfig: IAttachmentUploadProps["config"] = [
  {
    accept: ["image/jpeg", "image/png", "image/jpg"],
    size: 10 * 1024 * 1024,
  },
  {
    accept: ["application/pdf", ".xls", ".xlsx", ".doc", ".docx"],
    size: 50 * 1024 * 1024,
  },
];

export default function AttachmentUpload(props: IAttachmentUploadProps) {
  const {
    className,
    value = [],
    readonly,
    onChange,
    emptyElement = "--",
    config = defaultConfig,
    block = false,
    request = true,
    tips,
    maxCount,
    ...rest
  } = props;

  const [visible, setVisible] = useState({ src: undefined, value: false });

  const ref = useRef<HTMLDivElement>(null);
  const previewErrorRef = useRef<string>();

  const isEmptyConfig = Array.isArray(config) && config.length === 0;

  /**
   * 最大的图片数量限制
   */
  const max =
    config.some((c) => isNullOrUndefined(c.count)) || isEmptyConfig
      ? maxCount
      : Math.min(
          config.reduce((acc, cur) => acc + (cur.count || 0), 0),
          maxCount ?? Infinity
        );

  const accepts = isEmptyConfig
    ? undefined
    : config.map((item) => item.accept.join(",")).join(",");

  const [, upload] = useRequest<FormData, string>(apis.common.upload);

  const beforeUpload = (file: UploadFile) => {
    // 空数组不限制
    if (isEmptyConfig) {
      return true;
    }
    const cfg = config.find((i) => isAcceptFile(file, i.accept));
    if (!cfg) {
      const allAccepts = config.reduce(
        (pre, cur) => [...pre, ...cur.accept],
        []
      );
      message.error(`仅支持${getSuffixNamesByAccept(allAccepts)}格式`);
      return false;
    }
    if (file.size > (cfg.size ?? Infinity)) {
      message.error(`单个文件大小不超过${cfg.size / 1024 / 1024}M`);
      return false;
    }
    const count = value.filter((i) => isAcceptFile(i, cfg.accept)).length;
    if (count >= (cfg.count ?? Infinity)) {
      message.error(
        `${getSuffixNamesByAccept(cfg.accept)}文件数量不能超过${cfg.count}个`
      );
      return false;
    }
  };

  const onUpload: FileUploadProps["customRequest"] = ({
    file,
    onSuccess,
    onError,
  }) => {
    const formData = new FormData();
    formData.append("file", file);
    const tempUrl = URL.createObjectURL(file as Blob);
    if (!request) {
      onSuccess?.({ url: tempUrl });
      return;
    }
    upload(formData)
      .then((res) => onSuccess?.({ key: res, url: tempUrl }))
      .catch(onError);
  };

  const finilyFileList = useMemo<IAttachmentUploadProps["value"]>(
    () =>
      value.map((item) => ({
        status: "done",
        ...item,
        name: toLowerCaseSuffix(item.name),
      })),
    [value]
  );

  /**
   * 文件名尽量展示 组件库的逻辑只适用正常模式 当block模式是会展示不全 因此统一使用css展示
   */
  useEffect(() => {
    if (ref.current) {
      const fileNameElements = ref.current.querySelectorAll(".fileName");
      fileNameElements.forEach((ele) => {
        const title = ele.getAttribute("title");
        if (title) {
          const { name, suffix } = splitFileName(title);
          ele.innerHTML = `<span class="wbuc-attachment-upload-file-name">${name.slice(
            0,
            -2
          )}</span><span class="wbuc-attachment-upload-file-suffix">${name.slice(
            -2
          )}.${suffix}</span>`;
        }
      });
    }
  }, [finilyFileList?.map((i) => i.name + i.status).join("_")]);

  if (value.length === 0 && readonly) {
    return <>{emptyElement}</>;
  }

  return (
    <div
      ref={ref}
      className={classNames(
        "wbuc-attachment-upload",
        className,
        block && "wbuc-attachment-upload__block"
      )}
    >
      <FileUpload
        {...rest}
        showUploadList={readonly ? { showRemoveIcon: false } : true}
        maxCount={readonly ? 0 : max}
        fileList={finilyFileList}
        accept={accepts}
        beforeUpload={beforeUpload}
        customRequest={onUpload}
        onChange={({ file, fileList }) => {
          if (file.status === "error") {
            message.error("上传失败");
          }
          // 提出beforeUpload筛选后和error的结果
          onChange?.(
            fileList
              ?.filter((f) => f.status && f.status !== "error")
              .map((f) => ({
                ...f,
                url: f.response?.url,
                uid: f.response?.key ?? f.uid,
                name: toLowerCaseSuffix(f.name),
              }))
          );
        }}
        onPreview={(file) => {
          if (file.url) {
            return file.url === previewErrorRef.current
              ? previewWithNewWindow(file.url)
              : setVisible({ src: file.url, value: true });
          }
          const type = file.type || file.url?.split(".")?.pop();
          message.info(`${type}类型文件暂不支持预览`);
        }}
        onRemove={(file) => {
          if (file.url?.startsWith("blob:")) {
            URL.revokeObjectURL(file.url);
          }
          onChange?.(finilyFileList?.filter((f) => f?.uid !== file?.uid));
        }}
        onDownload={(file) => {
          const { url, name } = file || {};
          download(url, name);
        }}
        cancelUpload={(file) => {
          onChange?.(finilyFileList?.filter((f) => f?.uid !== file?.uid));
        }}
        uploadText={renderTips(tips)}
        renderIcon={(originNode, file) => {
          if (toLowerCaseSuffix(file.name).endsWith(".zip")) {
            return <img src={FileZip} alt="fileIcon" className="fileIcon" />;
          }
          return originNode;
        }}
      />
      <Image
        hidden
        src={visible.src}
        preview={{
          visible: visible.value,
          src: visible.src,
          onVisibleChange: (v) => {
            setVisible({ src: visible.src, value: v });
          },
        }}
        onError={() => {
          previewErrorRef.current = visible.src;
          previewWithNewWindow(visible.src);
          setVisible({ src: undefined, value: false });
        }}
      />
    </div>
  );
}

export { previewWithNewWindow };
